import { ClientTable } from "../../../components/ui/ClientTable";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import { cn } from "../../../lib/utils";
import { ui } from "../../../components/ui";
import { AUDIT_ACTION_LABEL_KEY } from "../constants/approvalConstants";
import { CLAIM_STATUS_LABEL_KEY, formatStamp } from "../constants/claimConstants";

/**
 * The claim's approval history — every action taken on it so far, oldest
 * first, read by the approver on Screen 6 and by the submitter in ESS.
 *
 * A plain table, one row per action: the level it was taken at, what was done,
 * by whom, the status it moved from and to, and when. Nothing is styled into a
 * shape the reader has to decode — the columns say what each value is, and a
 * value the claim does not carry is a dash.
 *
 * The reason sits on its own line beneath its action rather than in a column
 * of its own. It is free text an approver typed and can run to a paragraph;
 * given a column it would either squeeze the rest of the table or wrap into a
 * tall, ragged cell. Below the row it has the full width and costs nothing
 * when there is none, which is most rows.
 *
 * It is shown verbatim where one was recorded: Reject and Send Back always
 * carry one, Approve only if the approver chose to add a comment. An action
 * the label map does not know is rendered as it came back rather than dropped,
 * so a value added on the server side never silently disappears from an audit
 * trail.
 */

const headCell = "px-3 py-2.5 font-semibold whitespace-nowrap";
const cell = "px-3 py-2.5 text-[13px] per-file-hint align-top";

export function ClaimAuditTrail({ actions = [], loading = false }) {
  const { t } = useTranslation();

  /** A status as it reads everywhere else in the module. */
  const statusLabel = (status) =>
    status ? t(CLAIM_STATUS_LABEL_KEY[status] || status) : "—";

  if (loading) {
    return (
      <p className="px-4 sm:px-6 py-6 text-sm per-file-hint">
        {t("conveyanceApproval.loadingHistory")}
      </p>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="px-4 sm:px-6 py-8 text-center">
        <div className="mx-auto mb-2.5 h-10 w-10 rounded-2xl bg-workspace/60 grid place-items-center per-file-hint">
          <History className="h-5 w-5" />
        </div>
        <p className="text-sm per-file-hint">
          {t("conveyanceApproval.noHistory")}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <ClientTable className="w-full text-sm" style={{ minWidth: "720px" }}>
        <thead>
          <tr className={ui.tableHead}>
            <th className={headCell}>{t("conveyanceApproval.level")}</th>
            <th className={headCell}>{t("common.action")}</th>
            <th className={headCell}>{t("conveyanceApproval.actor")}</th>
            <th className={headCell}>{t("conveyanceApproval.from")}</th>
            <th className={headCell}>{t("conveyanceApproval.to")}</th>
            <th className={headCell}>{t("conveyanceClaim.date")}</th>
          </tr>
        </thead>
        <tbody>
          {actions.map((entry, index) => {
            const labelKey = AUDIT_ACTION_LABEL_KEY[entry.action];
            const rowKey = entry.claimActionId ?? entry.actionId ?? `${entry.action}-${index}`;
            return (
              <Fragment key={rowKey}>
              <tr
                className={cn(
                  "align-top",
                  index > 0 && "border-t border-(--color-text-border)/35"
                )}
              >
                <td className={`${cell} font-bold text-primary`}>
                  {entry.approvalLevel != null
                    ? t("conveyanceApproval.levelShort", { level: entry.approvalLevel })
                    : "—"}
                </td>
                <td className={`${cell} font-semibold text-foreground whitespace-nowrap`}>
                  {labelKey ? t(labelKey) : entry.action}
                </td>
                <td className={cell}>
                  {entry.actorName || t("conveyanceApproval.unknownActor")}
                  {entry.actorRoleAtAction ? (
                    <span className="block text-[11px]">{entry.actorRoleAtAction}</span>
                  ) : null}
                </td>
                <td className={cell}>{statusLabel(entry.fromStatus)}</td>
                <td className={`${cell} text-foreground`}>{statusLabel(entry.toStatus)}</td>
                <td className={`${cell} whitespace-nowrap`}>
                  {formatStamp(entry.actionOn, t) || "—"}
                </td>
              </tr>

              {entry.reasonComment ? (
                <tr>
                  <td colSpan={6} className="px-3 pb-3 text-[13px] leading-relaxed text-foreground/85 whitespace-pre-wrap wrap-break-word">
                    <span className="font-semibold per-file-hint">
                      {t("common.reason")}:{" "}
                    </span>
                    {entry.reasonComment}
                  </td>
                </tr>
              ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </ClientTable>
    </div>
  );
}

export default ClaimAuditTrail;
